// components/QuadrantPartners.tsx
"use client";

import * as React from "react";
import {
    Box,
    Grid,
    Paper,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function QuadrantPartners({ data }) {
    const theme = useTheme();

    // Equal space from all edges to the axes
    const inset = { xs: 20, sm: 28, md: 40 };

    // Extra breathing room next to the vertical divider
    // (theme.spacing(3) => 24px on md+)
    const centerGutter = 3;

    // Vertical line position: centered on mobile, 46% on desktop (your design)
    const vCenter = { xs: "50%", sm: "50%", md: "46%" };

    const Dot = () => (
        <Box
            sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "linear-gradient(180deg, #7fd0cb 0%, #0f7f8a 100%)",
                flex: "0 0 auto",
            }}
            aria-hidden
        />
    );

    const List = ({ items }) => (
        <Stack spacing={{ xs: 1.75, sm: 2, md: 2.25 }}>
            {items.map((t) => (
                <Stack key={t} direction="row" spacing={1.5} alignItems="center">
                    <Dot />
                    <Typography
                        sx={{
                            fontSize: { xs: 14, sm: 15, md: 16 },
                            color: theme.palette.text.primary,
                        }}
                    >
                        {t}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );

    // Responsive center-gutter for each column index
    const gutterSxFor = (idx) => ({
        // xs/sm: two columns per row -> add gutter around the center (between col 0 & 1, and 2 & 3)
        pr: { xs: idx % 2 === 0 ? centerGutter : 0, sm: idx % 2 === 0 ? centerGutter : 0, md: idx === 1 ? centerGutter : 0 },
        pl: { xs: idx % 2 === 1 ? centerGutter : 0, sm: idx % 2 === 1 ? centerGutter : 0, md: idx === 2 ? centerGutter : 0 },
    });

    return (
        <Box sx={{ p: { xs: 1.5, sm: 4 }, bgcolor: "#f5f7f8" }}>
            <Box sx={{ position: "relative", maxWidth: 1100, mx: "auto" }}>
                <Paper
                    elevation={0}
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 4,
                        border: "1px solid #E3E6EB",
                        p: { xs: 2.5, sm: 3.5, md: 5 },
                        backgroundColor: "#fff",

                        /* centered axes with equal inset on all sides */
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            left: inset,
                            right: inset,
                            top: "50%",
                            borderTop: "1px solid #1f2937",
                            zIndex: 1,
                            pointerEvents: "none",
                        },
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            top: inset,
                            bottom: inset,
                            left: vCenter,
                            borderLeft: "1px solid #1f2937",
                            zIndex: 1,
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* arrowheads */}
                    <KeyboardArrowLeftIcon
                        aria-hidden
                        sx={{
                            position: "absolute",
                            left: inset,
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            fontSize: 18,
                            color: "#1f2937",
                            pointerEvents: "none",
                            zIndex: 2,
                        }}
                    />
                    <KeyboardArrowRightIcon
                        aria-hidden
                        sx={{
                            position: "absolute",
                            right: inset,
                            top: "50%",
                            transform: "translate(50%, -50%)",
                            fontSize: 18,
                            color: "#1f2937",
                            pointerEvents: "none",
                            zIndex: 2,
                        }}
                    />
                    <KeyboardArrowUpIcon
                        aria-hidden
                        sx={{
                            position: "absolute",
                            top: inset,
                            left: vCenter,
                            transform: "translate(-50%, -50%)",
                            fontSize: 20,
                            color: "#1f2937",
                            pointerEvents: "none",
                            zIndex: 2,
                        }}
                    />
                    <KeyboardArrowDownIcon
                        aria-hidden
                        sx={{
                            position: "absolute",
                            bottom: inset,
                            left: vCenter,
                            transform: "translate(-50%, 50%)",
                            fontSize: 20,
                            color: "#1f2937",
                            pointerEvents: "none",
                            zIndex: 2,
                        }}
                    />

                    {/* content */}
                    <Grid
                        container
                        spacing={{ xs: 3, md: 4 }}
                        sx={{ position: "relative", zIndex: 3 }}
                    >
                        {data?.map((col, idx) => (
                            <Grid
                                key={idx}
                                item
                                xs={6}            // 2 columns on phones
                                sm={6}            // 2 columns on small tablets
                                md={3}            // 4 columns from md+
                                sx={gutterSxFor(idx)}
                            >
                                <List items={col.top} />
                                <Box sx={{ height: { xs: 14, md: 24 } }} />
                                <List items={col.bottom} />
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Box>
        </Box>
    );
}
